import type { CatalogIconKey } from '@/lib/icons';

/* ---------------- Feature / module system ---------------- */
export const FEATURE_KEYS = [
  'products', 'categories', 'cart', 'orders', 'payments', 'wallet', 'loyalty',
  'coupons', 'banners', 'reviews', 'referrals', 'affiliate', 'campaigns', 'analytics',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];
export type FeatureFlags = Record<FeatureKey, boolean>;

/* ---------------- Tenant ---------------- */
export interface NavItem {
  id: string;
  label: string;
  to: string;
  enabled: boolean;
  /** Item is hidden automatically when the tenant disables this module. */
  feature?: FeatureKey;
}

export interface TenantTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor?: string;
  textColor?: string;
  fontFamily: string;
  borderRadius: string; // css length, e.g. "0.75rem"
  defaultMode?: 'light' | 'dark';
  allowModeToggle?: boolean;
}

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  currency: 'INR';
  contact: { email: string; phone: string; address: string };
  social: { instagram?: string; facebook?: string; x?: string; youtube?: string };
  announcement?: string;
  theme: TenantTheme;
  features: FeatureFlags;
  shipping: { freeThreshold: number; flatFee: number };
  taxRate: number; // GST %, prices are tax-inclusive
  seo: { title: string; description: string };
  loyalty: { pointsPer100: number; pointValue: number; minRedeem: number };
  logoUrl?: string;
  navigation?: { header: NavItem[]; footer: { id: string; title: string; items: NavItem[] }[] };
}

/* ---------------- Catalog ---------------- */
export interface Category {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: CatalogIconKey;
  hue: number;
  productCount: number;
}

export interface ProductOption {
  name: string;
  values: string[];
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  brand: string;
  sku: string;
  description: string;
  highlights: string[];
  specs: { label: string; value: string }[];
  price: number;
  compareAtPrice?: number;
  categoryId: string;
  rating: number;
  reviewCount: number;
  stock: number;
  lowStockThreshold: number;
  images: string[]; // real image URLs; empty => themed illustration
  icon: CatalogIconKey;
  hue: number;
  options: ProductOption[];
  tags: string[];
  featured: boolean;
  createdAt: string;
}

export interface Review {
  id: string;
  productId: string;
  author: string;
  rating: number;
  title: string;
  body: string;
  date: string;
  verified: boolean;
  helpful: number;
}

export type SortKey = 'featured' | 'price-asc' | 'price-desc' | 'rating' | 'newest';

export interface ProductQuery {
  q?: string;
  categorySlug?: string;
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
  inStock?: boolean;
  onSale?: boolean;
  sort?: SortKey;
  page?: number;
  pageSize?: number;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/* ---------------- Cart / pricing ---------------- */
export interface CartLine {
  key: string; // productId + variant signature
  productId: string;
  slug: string;
  name: string;
  price: number;
  compareAtPrice?: number;
  icon: CatalogIconKey;
  hue: number;
  image?: string;
  quantity: number;
  maxQuantity: number;
  variant?: Record<string, string>;
  savedForLater?: boolean;
}

export type DiscountType = 'PERCENTAGE' | 'FIXED';
export interface Coupon {
  id: string;
  code: string;
  title: string;
  description: string;
  type: DiscountType;
  value: number;
  maxDiscount?: number;
  minOrder: number;
  expiresAt: string;
  usageLimit: number;
  usedCount: number;
  perCustomerLimit: number;
  categorySlugs?: string[];
}

export interface PriceBreakdown {
  subtotal: number;
  couponDiscount: number;
  walletApplied: number;
  pointsApplied: number;
  shipping: number;
  taxIncluded: number;
  total: number;
  payable: number; // total minus wallet/points
}

/* ---------------- Account ---------------- */
export type UserRole = 'SUPER_ADMIN' | 'TENANT_OWNER' | 'TENANT_ADMIN' | 'STAFF' | 'CUSTOMER';
export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: UserRole;
  tenantId: string;
  memberSince: string;
}

export interface Address {
  id: string;
  label: string;
  name: string;
  phone: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  pincode: string;
  isDefault?: boolean;
}

/* ---------------- Orders ---------------- */
export const ORDER_STATUSES = [
  'PENDING', 'CONFIRMED', 'PAID', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED', 'REFUNDED',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export type PaymentMethod = 'UPI' | 'CARD' | 'NETBANKING' | 'WALLET' | 'COD';

export interface OrderItem {
  productId: string;
  slug: string;
  name: string;
  price: number;
  quantity: number;
  icon: CatalogIconKey;
  hue: number;
  variant?: Record<string, string>;
}

export interface OrderEvent {
  status: OrderStatus;
  at: string;
  note?: string;
}

export interface Order {
  id: string;
  number: string;
  status: OrderStatus;
  placedAt: string;
  items: OrderItem[];
  pricing: PriceBreakdown;
  paymentMethod: PaymentMethod;
  paymentStatus: 'PENDING' | 'PAID' | 'REFUNDED' | 'FAILED';
  address: Address;
  couponCode?: string;
  timeline: OrderEvent[];
  tracking?: { carrier: string; awb: string };
  returnRequested?: boolean;
}

export interface PlaceOrderInput {
  lines: CartLine[];
  pricing: PriceBreakdown;
  address: Address;
  paymentMethod: PaymentMethod;
  couponCode?: string;
}

/* ---------------- Wallet / loyalty ---------------- */
export type WalletTxType = 'TOP_UP' | 'ORDER_PAYMENT' | 'REFUND' | 'REWARD' | 'ADJUSTMENT';
export interface WalletTransaction {
  id: string;
  type: WalletTxType;
  amount: number; // signed: + credit, - debit
  balanceAfter: number;
  description: string;
  createdAt: string;
}
export interface Wallet {
  balance: number;
  currency: 'INR';
}

export type LoyaltyTxType = 'EARN' | 'REDEEM' | 'BONUS' | 'EXPIRE';
export interface LoyaltyTransaction {
  id: string;
  type: LoyaltyTxType;
  points: number; // signed
  description: string;
  createdAt: string;
}
export interface LoyaltyAccount {
  balance: number;
  lifetimeEarned: number;
  lifetimeRedeemed: number;
  tier: string;
  nextTier?: { name: string; pointsNeeded: number };
  earnRules: { id: string; label: string; points: number; done?: boolean }[];
  rewards: { id: string; title: string; cost: number; description: string }[];
}

/* ---------------- Marketing / CMS ---------------- */
export interface Banner {
  id: string;
  placement: 'HERO' | 'PROMO' | 'DEAL';
  eyebrow?: string;
  title: string;
  subtitle: string;
  cta: { label: string; to: string };
  secondaryCta?: { label: string; to: string };
  hue: number;
  icon: CatalogIconKey;
}

export interface Testimonial {
  id: string;
  name: string;
  city: string;
  rating: number;
  quote: string;
}

export interface AffiliateSummary {
  referralCode: string;
  referralLink: string;
  clicks: number;
  signups: number;
  conversions: number;
  commissionRate: number;
  pendingCommission: number;
  paidCommission: number;
  referrals: { id: string; name: string; status: 'SIGNED_UP' | 'PURCHASED'; commission: number; date: string }[];
}
